import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { GET_ALL_CITIES } from '../utils/queries';
import { areCitiesEquivalent } from '../config/cityData';

interface City {
  id: string;
  name: string;
  showBookNow: boolean;
}

interface CitiesContextType {
  cities: City[];
  loading: boolean;
  error: string | null;
  getCityShowBookNow: (cityName: string) => boolean;
}

const CitiesContext = createContext<CitiesContextType | undefined>(undefined);

interface CitiesProviderProps {
  children: ReactNode;
}

const TURF_URL = process.env.NEXT_PUBLIC_TURF_URL ?? "";

export const CitiesProvider: React.FC<CitiesProviderProps> = ({ children }) => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const response = await axios.post(TURF_URL + `/graphql`, {
        query: GET_ALL_CITIES,
        variables: {},
      });
      
      if (response.data?.data?.cities) {
        setCities(response.data.data.cities);
        setError(null);
      } else {
        setError("No cities data received");
      }
    } catch (err) {
      console.error("Error fetching cities:", err);
      setError("Failed to fetch cities");
    } finally {
      setLoading(false);
    }
  };

  const getCityShowBookNow = (cityName: string): boolean => {
    if (!cityName || !cities.length) return false;
    
    const city = cities.find(c => 
      areCitiesEquivalent(c.name, cityName)
    );
    
    return city?.showBookNow ?? false;
  };

  useEffect(() => {
    fetchCities();
  }, []);

  const value: CitiesContextType = {
    cities,
    loading,
    error,
    getCityShowBookNow,
  };

  return (
    <CitiesContext.Provider value={value}>
      {children}
    </CitiesContext.Provider>
  );
};

export const useCities = (): CitiesContextType => {
  const context = useContext(CitiesContext);
  if (context === undefined) {
    throw new Error('useCities must be used within a CitiesProvider');
  }
  return context;
};
